// src/components/Map/Map.tsx
import './Map.scss';
import React, {
  FC,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import ReactDOMServer from 'react-dom/server';
import { TSMap } from 'typescript-map';

import { useType } from '../app/App';
import { GeneralContextType } from '../../context/GeneralContext';
import { MapContext } from '../../context/MapContext';

import normalIcon from '../../res/svg/normal.svg';
import stopIcon from '../../res/svg/stop.svg';
import privateIcon from '../../res/svg/private.svg';
import stationnaryIcon from '../../res/svg/stationnary.svg';

import { getDateDayMonthYear, getHour } from '../../utils/MomentUtils';
import { getAddress } from '../../utils/MapsUtils';
import { WsData } from '../../interface/interface';

/** Component props */
interface MapProps {
  options: google.maps.MapOptions;
  markers: Map<number, WsData> | undefined;
  wsData: any;
  /** Padding (px) appliqué lors des fitBounds. Peut être un nombre (uniforme) ou un objet. */
  viewportPadding?: number | Partial<google.maps.Padding>;
  /** Nombre d'étapes de dézoom après fitBounds (par défaut 1). */
  extraZoomOutSteps?: number;
}

/** Padding par défaut : espace pour la card en bas. */
const DEFAULT_PADDING: google.maps.Padding = {
  top: 32,
  right: 32,
  bottom: 280,
  left: 32,
};

/** Convertit un paramètre de padding en google.maps.Padding complet. */
const toFullPadding = (
  p?: number | Partial<google.maps.Padding>
): google.maps.Padding => {
  if (typeof p === 'number') {
    return { top: p, right: p, bottom: p, left: p };
  }
  return {
    top: p?.top ?? DEFAULT_PADDING.top,
    right: p?.right ?? DEFAULT_PADDING.right,
    bottom: p?.bottom ?? DEFAULT_PADDING.bottom,
    left: p?.left ?? DEFAULT_PADDING.left,
  };
};

/** Applique fitBounds avec padding asymétrique. */
const fitBoundsWithPadding = (
  m: google.maps.Map,
  b: google.maps.LatLngBounds,
  padding?: number | Partial<google.maps.Padding>
) => {
  m.fitBounds(b, toFullPadding(padding));
};

const Map: FC<MapProps> = ({ options, markers, wsData, viewportPadding, extraZoomOutSteps = 1 }) => {
  const mapContext = useContext(MapContext);
  const mapRef = useRef<HTMLDivElement>(null);

  const [markerRefs, setMarkerRefs] = useState<TSMap<number, google.maps.Marker>>(new TSMap());
  const [infoWindowRefs, setInfoWindowRefs] = useState<TSMap<number, google.maps.InfoWindow>>(new TSMap());

  const directionsService = useRef<google.maps.DirectionsService>();
  const directionsRenderer = useRef<google.maps.DirectionsRenderer | null>(null);

  const [startMarker, setStartMarker] = useState<google.maps.Marker | null>(null);
  const [endMarker, setEndMarker] = useState<google.maps.Marker | null>(null);

  const type: GeneralContextType | null = useType();
  const [oldMarkers, setOldMarkers] = useState<Map<number, WsData>>();

  // Initialise la map une fois que Google Maps est disponible
  const map = useMemo(() => {
    if (typeof window === 'undefined') return null;
    if (typeof window.google === 'undefined' || typeof window.google.maps === 'undefined') {
      console.warn("Google Maps API isn't available");
      return null;
    }
    if (!mapRef.current) return null;
    return new window.google.maps.Map(mapRef.current, options);
  }, [mapRef.current, options]);

  /** Retourne l'icône du marqueur selon l'état du véhicule. */
  const marketicon = (marker?: WsData): string => {
    if (!marker) return normalIcon;
    if (marker.private) return privateIcon;
    if (marker.idle) return stopIcon;
    return normalIcon;
  };

  /** Pop-up PS (position/état) */
  const popupContentPS = (idVehicle: number) => {
    const marker = markers?.get(idVehicle);
    const markerGoogle = markerRefs.get(idVehicle);
    if (!marker || !marker.gendate || !markerGoogle) return;

    const date = `${getDateDayMonthYear(new Date(marker.gendate * 1000) as any)} - ${getHour(new Date(marker.gendate * 1000) as any)}`;
    const temps =
      marker.temperatures?.map(t => `${t.value} °C`).join(', ') ?? '';

    const allContent = (
      <div className="flexHorizontal gap8 poppins">
        <svg width="15" height="23" viewBox="0 0 25 25">
          <path
            fill="#2196F3FF"
            d="M12 0c-6.63 0-12 5.37-12 12 0 8.45 11.38 19.29 11.84 19.77 0.21 0.2 0.49 0.31 0.77 0.31s0.56-0.11 0.77-0.31c0.46-0.48 11.84-11.32 11.84-19.77 0-6.63-5.37-12-12-12zM12 18.75c-2.97 0-5.39-2.42-5.39-5.39s2.42-5.39 5.39-5.39 5.39 2.42 5.39 5.39-2.42 5.39-5.39 5.39z"
          ></path>
        </svg>
        <div>
          <h2 className="fontSize12 secondaryColor">{marker?.registration}</h2>
          <p className="fontSize10 tertiaryColor">{marker.address}</p>
          <p className="fontSize10 tertiaryColor">{date}</p>
          {temps && <p className="fontSize10 tertiaryColor">{temps}</p>}
        </div>
      </div>
    );
    return allContent;
  };

  /** Pop-up ETA départ */
  const popupContentETA = (idVehicle: number) => {
    const marker = markers?.get(idVehicle);
    if (!marker) return;
    const allContent = (
      <div className="flexHorizontal gap8 poppins">
        <svg width="15" height="23" viewBox="0 0 25 25">
          <path
            fill="#2196F3FF"
            d="M12 0c-6.63 0-12 5.37-12 12 0 8.45 11.38 19.29 11.84 19.77 0.21 0.2 0.49 0.31 0.77 0.31s0.56-0.11 0.77-0.31c0.46-0.48 11.84-11.32 11.84-19.77 0-6.63-5.37-12-12-12zM12 18.75c-2.97 0-5.39-2.42-5.39-5.39s2.42-5.39 5.39-5.39 5.39 2.42 5.39 5.39-2.42 5.39-5.39 5.39z"
          ></path>
        </svg>
        <div>
          <h2 className="fontSize12 secondaryColor">{marker.address}</h2>
        </div>
      </div>
    );
    return allContent;
  };

  /** Pop-up ETA destination (reverse geocode async) */
  const popupContentETADestination = async (idVehicle: number) => {
    const marker = markers?.get(idVehicle);
    if (!marker || !marker.gendate || !marker.endPosition) return null;

    try {
      const addr = await getAddress(marker.endPosition.lat, marker.endPosition.lng);
      const allContent = (
        <div className="flexHorizontal gap8 poppins">
          <svg width="15" height="23" viewBox="0 0 25 25">
            <path
              fill="#2196F3FF"
              d="M12 0c-6.63 0-12 5.37-12 12 0 8.45 11.38 19.29 11.84 19.77 0.21 0.2 0.49 0.31 0.77 0.31s0.56-0.11 0.77-0.31c0.46-0.48 11.84-11.32 11.84-19.77 0-6.63-5.37-12-12-12zM12 18.75c-2.97 0-5.39-2.42-5.39-5.39s2.42-5.39 5.39-5.39 5.39 2.42 5.39 5.39-2.42 5.39-5.39 5.39z"
            ></path>
          </svg>
          <div>
            <h2 className="fontSize12 secondaryColor">{addr}</h2>
          </div>
        </div>
      );
      return allContent;
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  /** Crée un nouveau Marker + InfoWindow, et centre avec padding. */
  const createMarker = (idVehicle: number) => {
    if (!markers || !map) return;

    const value = markers.get(idVehicle);
    if (!value?.position) return;

    const newMarker = new google.maps.Marker({
      position: value.position,
      map: map,
      icon: marketicon(value),
    });

    markerRefs.set(idVehicle, newMarker);
    infoWindowRefs.set(idVehicle, new google.maps.InfoWindow());

    const content = popupContentPS(idVehicle);
    if (content) {
      infoWindowRefs.get(idVehicle).setContent(ReactDOMServer.renderToString(content));
    }

    newMarker.addListener('click', () => {
      infoWindowRefs.get(idVehicle).open({ anchor: newMarker, map });
    });

    // Centrage avec padding
    const b = new google.maps.LatLngBounds();
    b.extend(value.position);
    fitBoundsWithPadding(map, b, viewportPadding);
  };

  /** Met à jour un marker existant (position, icône, contenu IW). */
  const modifiedMarker = (point: WsData) => {
    if (!markers || !markerRefs) return;

    const markerGoogle = markerRefs.get(point.id);
    if (!markerGoogle) return;

    const hasPosition = Boolean(point.position?.lat && point.position?.lng);

    if (hasPosition) {
      markerGoogle.setPosition(point.position);
    }
    markerGoogle.setIcon(marketicon(point));

    setMarkerRefs(prev => {
      markerRefs.set(point.id, markerGoogle);
      return markerRefs;
    });

    const content = popupContentPS(point.id);
    if (hasPosition && content) {
      infoWindowRefs.get(point.id).setContent(ReactDOMServer.renderToString(content));
    }
  };

  /** Crée l'itinéraire et ajuste le viewport avec padding + dézoom léger. */
  const createRoute = () => {
    if (!markers || !map) return;

    const point = markers.get(wsData?.vehicleId);
    const hasPosition = Boolean(point && point.position?.lng && point.position?.lat);
    if (!hasPosition || !point) return;

    // Reset previous start/end markers
    if (startMarker) { startMarker.setMap(null); setStartMarker(null); }
    if (endMarker) { endMarker.setMap(null); setEndMarker(null); }

    // Reset previous renderer
    if (directionsRenderer.current) {
      directionsRenderer.current.setMap(null);
      directionsRenderer.current = null;
    }

    directionsService.current = new google.maps.DirectionsService();
    directionsRenderer.current = new google.maps.DirectionsRenderer({
      map: map,
      suppressMarkers: true,
      preserveViewport: true, // on maîtrise le viewport
    });

    if (point.endPosition) {
      const newStartMarker = new google.maps.Marker({
        position: point.position,
        icon: marketicon(point),
        map: map,
      });

      const newEndMarker = new google.maps.Marker({
        position: point.endPosition,
        icon: {
          path: 'M 0,0 m -12,0 a 12,12 0 1,0 24,0 a 12,12 0 1,0 -24,0',
          fillColor: '#2196F3',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 4,
          scale: 1,
        },
        map: map,
      });

      setStartMarker(newStartMarker);
      setEndMarker(newEndMarker);

      const request: google.maps.DirectionsRequest = {
        origin: point.position,
        destination: point.endPosition,
        travelMode: google.maps.TravelMode.DRIVING,
      };

      directionsService.current.route(request, (response, status) => {
        if (status === google.maps.DirectionsStatus.OK && directionsRenderer.current) {
          directionsRenderer.current.setDirections(response);

          const route = response?.routes?.[0];
          const routeBounds = route?.bounds;
          if (routeBounds) {
            // 1) Fit avec padding (bottom pour la card)
            fitBoundsWithPadding(map, routeBounds, viewportPadding);

            // 2) Dézoom léger optionnel
            if (extraZoomOutSteps > 0) {
              setTimeout(() => {
                const z = map.getZoom() ?? 15;
                map.setZoom(Math.max(0, z - extraZoomOutSteps));
              }, 0);
            }
          }
        }
      });

      // InfoWindows ETA
      infoWindowRefs.set(wsData.vehicleId, new google.maps.InfoWindow());

      newStartMarker.addListener('click', async () => {
        infoWindowRefs.get(wsData.vehicleId).open({ anchor: newStartMarker, map });
        const content = await popupContentETA(wsData.vehicleId);
        if (content) {
          infoWindowRefs.get(wsData.vehicleId).setContent(ReactDOMServer.renderToString(content));
        }
      });

      newEndMarker.addListener('click', async () => {
        infoWindowRefs.get(wsData.vehicleId).open({ anchor: newEndMarker, map });
        const content = await popupContentETADestination(wsData.vehicleId);
        if (content) {
          infoWindowRefs.get(wsData.vehicleId).setContent(ReactDOMServer.renderToString(content));
        }
      });
    }
  };

  /** Zoom sur un marker via sa registration. */
  const zoomToMarker = (markerSelect: string | undefined) => {
    if (!markers || !map) return;

    const marker = Array.from(markers.values()).find(m => m.registration === markerSelect);
    if (!marker?.position) return;

    map.setCenter(marker.position);
    map.setZoom(15);
  };

  // Gestion PS vs ETA à chaque mise à jour des markers
  useEffect(() => {
    if (!mapRef.current || !map) return;

    if (type?.type === 'ps') {
      if (markers !== undefined) {
        if (oldMarkers === undefined) {
          // Initialisation : créer tous les marqueurs
          markers.forEach((_, key) => createMarker(key));
          setOldMarkers(markers);
        } else {
          // Diff incrémental
          markers.forEach((value, key) => {
            if (oldMarkers.has(key)) {
              if (oldMarkers.get(key) !== value) {
                modifiedMarker(value);
              }
            } else {
              createMarker(key);
            }
          });
          setOldMarkers(markers);
        }
      }
    } else {
      // Affichage ETA (itinéraire)
      createRoute();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markers]);

  // Zoom par sélection (registration)
  useEffect(() => {
    if (mapRef.current && (mapContext?.registration !== '' || mapContext?.registration !== undefined)) {
      zoomToMarker(mapContext?.registration);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapContext?.registration]);

  return (
    <div className="width100 height100 zIndex1 relative">
      <div id="map" ref={mapRef} className="width100 height100 relative"></div>
    </div>
  );
};

export default Map;